// AiMessageList — renderar chatten i AI-assistenten: tom-state,
// hydrerings-skeleton vid conversation-switch, alla meddelanden
// (markdown + option-knappar + JSON-import-knapp + per-message-error),
// thinking-indikator, moderation-flag-bubble och intent-konfirmation.
// Extraherad ur AiAssistantDialog (#1 i PRE_RELEASE_REVIEW_100).

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Sparkles, Download, AlertCircle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ModerationFlagMessage } from '@/components/workflow/ModerationFlagMessage';
import { IntentConfirmationBanner } from '@/components/workflow/IntentConfirmationBanner';

// #28: visa "Importerad"-state i 2 s efter klick så user inte
// dubbelklickar och får två stagings/imports av samma proposal.
function ImportToCanvasButton({ jsonStr, onImportJson }: {
  jsonStr: string;
  onImportJson: (s: string) => void;
}) {
  const [justImported, setJustImported] = useState(false);
  return (
    <Button
      size="sm"
      variant="secondary"
      className="mt-2 gap-2"
      disabled={justImported}
      onClick={() => {
        onImportJson(jsonStr);
        setJustImported(true);
        setTimeout(() => setJustImported(false), 2000);
      }}
    >
      {justImported ? (
        <>
          <Check className="w-3.5 h-3.5 text-green-500" />
          Imported
        </>
      ) : (
        <>
          <Download className="w-3.5 h-3.5" />
          Import to canvas
        </>
      )}
    </Button>
  );
}
import {
  extractJsonBlocks,
  extractOptions,
  stripOptions,
  stripJsonBlocksStreaming,
  type Msg,
} from '@/lib/aiMessageHelpers';
import type { IntentClassification, Intent } from '@/lib/intentTypes';

interface AiMessageListProps {
  messages: Msg[];
  isLoading: boolean;
  activeConversationId: string | null;
  loadedMessages: unknown[] | undefined;
  configEndpoint: string;
  configModel: string;
  strikes: number;
  maxStrikes: number;
  activeFlag: {
    ruleId: string;
    severity: 'advisory' | 'violation';
    humanReadable: string;
    correlationId: string;
    eventId?: string;
  } | null;
  pendingConfirm: { prompt: string; classification: IntentClassification } | null;
  onOptionClick: (option: string) => void;
  onImportJson: (jsonStr: string) => void;
  onAppealClick: (args: { eventId?: string; ruleId: string; correlationId: string }) => void;
  onConfirmIntent: (intent: Intent) => void;
  onCancelIntent: () => void;
  /** #24: ifyll prompt-input direkt när user klickar en example chip. */
  onPickExample?: (prompt: string) => void;
}

// #24: Empty-state example chips. Free-form text fields convert poorly;
// concrete starter prompts give the user a tangible first action.
const EMPTY_STATE_EXAMPLES = [
  'Skapa ett login-flöde med 3 block',
  'Dokumentera det här arbetsflödet',
  'Lägg till felhanterings-grenar på alla servicekall',
];

export function AiMessageList({
  messages,
  isLoading,
  activeConversationId,
  loadedMessages,
  configEndpoint,
  configModel,
  strikes,
  maxStrikes,
  activeFlag,
  pendingConfirm,
  onOptionClick,
  onImportJson,
  onAppealClick,
  onConfirmIntent,
  onCancelIntent,
  onPickExample,
}: AiMessageListProps) {
  return (
    <div className="space-y-4 p-4">
      {/* Loading-skeleton när user just switchat till sparad conversation
          men list_conversation_messages ännu inte returnerat. Effecten
          clearar messages direkt vid switch — skeleton fyller gapet så
          empty-state inte flashar fel. */}
      {activeConversationId && !loadedMessages && messages.length === 0 && (
        <div className="space-y-3 py-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`flex ${i % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
              <div className={`h-12 rounded-xl bg-muted animate-pulse ${i % 2 === 0 ? 'w-2/3' : 'w-1/2'}`} />
            </div>
          ))}
        </div>
      )}
      {messages.length === 0 && !(activeConversationId && !loadedMessages) && (
        <div className="text-center text-muted-foreground text-sm py-8">
          <Sparkles className="w-8 h-8 mx-auto mb-3 text-primary" />
          <p className="font-medium">AI Workflow Assistant</p>
          <p className="mt-1">Describe what you want to create and I&apos;ll help you build it!</p>
          {onPickExample && (
            <div className="flex flex-wrap gap-2 justify-center mt-4 px-4">
              {EMPTY_STATE_EXAMPLES.map((example) => (
                <Button
                  key={example}
                  size="sm"
                  variant="outline"
                  className="text-xs h-auto py-1.5 px-3 whitespace-normal text-left"
                  onClick={() => onPickExample(example)}
                  data-testid="ai-empty-example-chip"
                >
                  {example}
                </Button>
              ))}
            </div>
          )}
          {configEndpoint && (
            <p className="mt-3 text-xs text-muted-foreground/60">
              Connected to {configEndpoint}{configModel ? ` (${configModel})` : ''}
            </p>
          )}
        </div>
      )}
      {messages.map((msg, i) => (
        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
            msg.role === 'user'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          }`}>
            {msg.role === 'assistant' ? (
              <div>
                <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                  {/* JSON-fences göms från chatten — import-knappen
                      exponerar JSON via onImportJson (inte synligt). */}
                  <ReactMarkdown>{stripJsonBlocksStreaming(stripOptions(msg.content))}</ReactMarkdown>
                </div>
                {!isLoading && extractOptions(msg.content).length > 0 && i === messages.length - 1 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {extractOptions(msg.content).map((opt, j) => (
                      <Button
                        key={j}
                        size="sm"
                        variant="outline"
                        className="text-xs h-auto py-1.5 px-3 whitespace-normal text-left"
                        onClick={() => onOptionClick(opt)}
                      >
                        {opt}
                      </Button>
                    ))}
                  </div>
                )}
                {extractJsonBlocks(msg.content).map((jsonStr, j) => (
                  <ImportToCanvasButton
                    key={j}
                    jsonStr={jsonStr}
                    onImportJson={onImportJson}
                  />
                ))}
                {msg.error && (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-2.5 py-1.5 text-xs text-destructive">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <span className="break-words">{msg.error}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="whitespace-pre-wrap">{msg.content}</p>
            )}
          </div>
        </div>
      ))}
      {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
        <div className="flex justify-start">
          <div className="bg-muted rounded-xl px-4 py-2.5 text-sm">
            <span className="animate-pulse">Thinking...</span>
          </div>
        </div>
      )}
      {activeFlag && (
        <ModerationFlagMessage
          ruleId={activeFlag.ruleId}
          severity={activeFlag.severity}
          humanReadable={activeFlag.humanReadable}
          correlationId={activeFlag.correlationId}
          eventId={activeFlag.eventId}
          onAppeal={onAppealClick}
          contextLine={
            activeFlag.severity === 'violation' && strikes > 0
              ? `Strike ${strikes}/${maxStrikes} inom senaste 24 h.`
              : undefined
          }
        />
      )}
      {pendingConfirm && (
        <IntentConfirmationBanner
          suggestedIntent={pendingConfirm.classification.intent}
          confidence={pendingConfirm.classification.confidence}
          reasoning={pendingConfirm.classification.reasoning}
          onConfirm={onConfirmIntent}
          onCancel={onCancelIntent}
          onChangeTo={onConfirmIntent}
        />
      )}
    </div>
  );
}
